import { FadeUp, AnimatedLine, StaggerContainer, StaggerItem } from "../components/Animations";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const InvestmentPhilosophyPage = () => {
  const principles = [
    {
      number: "01",
      title: "Capital Discipline",
      content: "Every deployment begins with a clear understanding of downside scenarios. We preserve capital first, then seek returns. This hierarchy is non-negotiable regardless of market enthusiasm or competitive pressure.",
    },
    {
      number: "02",
      title: "Risk Management",
      content: "Risk is not a number to be minimized but a reality to be understood. We assess risks qualitatively and quantitatively, sizing positions to reflect both conviction and uncertainty. Concentration where warranted, diversification where prudent.",
    },
    {
      number: "03",
      title: "Liquidity Awareness",
      content: "We maintain the flexibility to act when opportunities arise and to endure when markets seize. Permanent capital allows us to be providers of liquidity to others, not dependent on their willingness to provide it to us.",
    },
    {
      number: "04",
      title: "Market Cycles",
      content: "Markets oscillate between fear and greed with remarkable regularity. We position portfolios to withstand inevitable corrections while maintaining the capacity to deploy capital when pessimism creates opportunity.",
    },
    {
      number: "05",
      title: "Human-Led Decision Making",
      content: "Algorithms inform but do not decide. Judgment, experience, and wisdom—accumulated through cycles of success and failure—remain at the center of our process. We embrace tools that enhance human judgment, not replace it.",
    },
    {
      number: "06",
      title: "Macro Awareness",
      content: "Individual investments exist within broader systems. We consider geopolitical dynamics, monetary policy regimes, and structural economic shifts when positioning the portfolio. Context shapes returns.",
    },
  ];

  return (
    <div data-testid="investment-philosophy-page">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-6 block">Philosophy</span>
            <h1 className="text-display text-charcoal max-w-5xl">
              Investment Philosophy
            </h1>
          </FadeUp>

          <FadeUp delay={0.2} className="mt-12 max-w-3xl">
            <p className="text-xl md:text-2xl font-sans font-light text-stone leading-relaxed">
              Our approach to capital allocation is grounded in principles refined 
              through multiple market cycles and decades of institutional experience.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Opening Statement */}
      <section className="pb-24 md:pb-32">
        <div className="container-wide">
          <div className="max-w-4xl">
            <FadeUp>
              <p className="text-2xl md:text-3xl font-serif text-charcoal leading-relaxed">
                We believe that exceptional long-term returns are the consequence of 
                disciplined process, not the product of exceptional foresight.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section-spacing bg-white grain">
        <div className="container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-6 block">Core Principles</span>
            <h2 className="text-headline text-charcoal mb-16">
              Foundations of our approach.
            </h2>
          </FadeUp>

          <StaggerContainer className="space-y-0" staggerDelay={0.1}>
            {principles.map((principle) => (
              <StaggerItem key={principle.number}>
                <AnimatedLine />
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 py-12 md:py-16">
                  <div className="md:col-span-1">
                    <span className="text-mono text-bronze">{principle.number}</span>
                  </div>
                  <div className="md:col-span-4">
                    <h3 className="text-2xl md:text-3xl font-serif text-charcoal">
                      {principle.title}
                    </h3>
                  </div>
                  <div className="md:col-span-6 md:col-start-7">
                    <p className="text-body text-lg">{principle.content}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
            <AnimatedLine />
          </StaggerContainer>
        </div>
      </section>

      {/* Manifesto Section */}
      <section className="section-spacing">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto text-center">
            <FadeUp>
              <h2 className="text-headline text-charcoal mb-12">
                What we seek, what we avoid.
              </h2>
            </FadeUp>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 text-left">
              <FadeUp delay={0.1}>
                <h4 className="text-sm font-sans uppercase tracking-wider text-bronze mb-6">We Seek</h4>
                <ul className="space-y-4">
                  <li className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-charcoal mt-2 flex-shrink-0" />
                    <span className="text-body">Asymmetric return profiles</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-charcoal mt-2 flex-shrink-0" />
                    <span className="text-body">Durable competitive advantages</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-charcoal mt-2 flex-shrink-0" />
                    <span className="text-body">Aligned management teams</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-charcoal mt-2 flex-shrink-0" />
                    <span className="text-body">Tangible asset backing</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-charcoal mt-2 flex-shrink-0" />
                    <span className="text-body">Margin of safety</span>
                  </li>
                </ul>
              </FadeUp>

              <FadeUp delay={0.2}>
                <h4 className="text-sm font-sans uppercase tracking-wider text-stone/60 mb-6">We Avoid</h4>
                <ul className="space-y-4">
                  <li className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-stone/40 mt-2 flex-shrink-0" />
                    <span className="text-body text-stone">Leverage-dependent returns</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-stone/40 mt-2 flex-shrink-0" />
                    <span className="text-body text-stone">Crowded positions</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-stone/40 mt-2 flex-shrink-0" />
                    <span className="text-body text-stone">Complexity without compensation</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-stone/40 mt-2 flex-shrink-0" />
                    <span className="text-body text-stone">Trend-driven narratives</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-stone/40 mt-2 flex-shrink-0" />
                    <span className="text-body text-stone">Short-term optimization</span>
                  </li>
                </ul>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="section-spacing bg-charcoal">
        <div className="container-wide text-center">
          <FadeUp>
            <p className="text-2xl md:text-3xl font-serif text-champagne max-w-3xl mx-auto leading-relaxed">
              The compounding of capital over time requires the compounding of 
              discipline over time.
            </p>
          </FadeUp>

          <FadeUp delay={0.2} className="mt-12">
            <Link
              to="/legacy"
              className="inline-flex items-center gap-3 text-champagne hover:text-bronze transition-colors duration-500"
              data-testid="explore-legacy-link"
            >
              <span className="text-sm font-sans font-medium uppercase tracking-wider">Explore Our Legacy</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default InvestmentPhilosophyPage;
